import {
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewEncapsulation,
  inject,
  input,
  model,
  output,
  signal,
  viewChildren
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormsModule, NgModel } from '@angular/forms';
import { TranslateDirective } from '@ngx-translate/core';
import { skip } from 'rxjs/operators';

import { TranslatePipe } from '../../pipes/translate.pipe';
import { DropdownComponent } from '../../components/dropdown/dropdown.component';
import { DropdownItemComponent } from '../../components/dropdown/dropdown-item.component';

import { FormRowComponent } from '../../components/form/row/form-row.component';
import { InputDirective } from '../../components/input/input.directive';
import { InputErrorsComponent } from '../../components/form/input-errors/input-errors.component';
import { InputStatusIndicatorDirective } from '../../components/form/input-status/input-status-indicator.directive';
import { InputGroupComponent } from '../../components/form/input-group/input-group.component';
import { InputGroupLabelComponent } from '../../components/form/input-group/input-group-label.component';
import { InputGroupFieldComponent } from '../../components/form/input-group/input-group-field.component';
import { RuralRoutePoBoxInputDirective } from '../../components/input/rural-route-po-box-input.directive';

import { CallOutComponent } from '../call-out/call-out.component';
import { PostalCodeValidatorDirective } from '../postal-code/postal-code-validator.directive';
import { ValueAccessorProviderFactory } from '../../components/value-accessor/value-accessor-provider-factory';

import { Address } from '../../domain/address.domain';
import { AddressService } from './address.service';

type SubmitTrackingFormControl = AbstractControl & { submitted?: boolean };

@Component({
  selector: 'td-wb-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss', './address-autocomplete.scss'],
  providers: [AddressService, ValueAccessorProviderFactory(AddressComponent)],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CallOutComponent,
    TranslatePipe,
    FormRowComponent,
    InputDirective,
    InputErrorsComponent,
    InputGroupComponent,
    InputGroupLabelComponent,
    InputGroupFieldComponent,
    InputStatusIndicatorDirective,
    FormsModule,
    DropdownComponent,
    DropdownItemComponent,
    PostalCodeValidatorDirective,
    CallOutComponent,
    TranslateDirective,
    RuralRoutePoBoxInputDirective
  ]
})
export class AddressComponent implements OnInit, ControlValueAccessor {
  private static createEmptyAddress(): Address {
    return {
      streetName: '',
      streetNum: '',
      unitNum: '',
      poBox: '',
      cityName: '',
      territory: '',
      postalCode: '',
      addressLine: [''],
      territoryCd: '',
      countryCd: '',
      postalStationTxt: ''
    };
  }

  private readonly addressService = inject(AddressService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly addressModels = viewChildren(NgModel);
  private readonly disabledByOwner = signal(false);

  readonly disabled = input<boolean>(undefined);
  readonly usageId = input<string>('home');
  readonly showTitleAndCallout = input<boolean>(true);
  readonly showRuralRouteAndPOBox = input<boolean>(true);

  /** Signal Forms native custom-control value. */
  readonly value = model<Address | null>(null);

  /** Signal Forms native custom-control touch notification. */
  readonly touch = output<void>();

  private onChangeCallback: (value: Address | null) => void = (): void => {};
  private onTouchedCallback: () => void = (): void => {};

  protected streetNumDisplay: string = '';
  protected ruralOrPoBox: string = '';
  protected ruralOrPoBoxType: string = '';
  protected selectedAddress: Address = AddressComponent.createEmptyAddress();

  ngOnInit(): void {
    this.addressService.loadAddressComplete(this.usageId());
    this.addressService.onSelectedAddressChange
      .pipe(skip(1))
      .subscribe((selectedAddress: Address): void => {
        this.setSelectedAddress(selectedAddress);
        this.emitAddressValue(true);
        this.changeDetectorRef.markForCheck();
      });
  }

  writeValue(value: Address | null | undefined): void {
    const nextValue = value ? this.cloneAddress(value) : AddressComponent.createEmptyAddress();
    this.setSelectedAddress(nextValue);
    this.value.set(value ? this.cloneAddress(value) : null);
  }

  registerOnChange(fn: (value: Address | null) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByOwner.set(isDisabled);
    this.changeDetectorRef.markForCheck();
  }

  getProvinces(): ReadonlyArray<readonly [string, string]> {
    return Array.from(this.addressService.provinces.entries());
  }

  getRuralPoTypes(): Array<string> {
    return this.addressService.RURAL_PO_TYPES;
  }

  getSelectedAddress(): Address {

    // populate steet number suffix if it has it
    this.selectedAddress.streetNumSuffixVal = this.addressService.parseStreetNumberSuffix(this.streetNumDisplay);
    this.selectedAddress.streetNum = this.addressService.parseStreetNumber(this.streetNumDisplay);

    if (this.usageId() === 'home' && this.ruralOrPoBox) {
      this.ruralOrPoBoxType = AddressService.RURAL_ROUTE_TYPE;
    }

    if (this.showRuralRouteAndPOBox()) {
      this.selectedAddress.ruralRouteTypeCd = this.ruralOrPoBoxType;
      if (this.ruralOrPoBoxType === AddressService.PO_BOX_TYPE) {
        this.selectedAddress.poBox = this.ruralOrPoBox;
        this.selectedAddress.ruralRouteNum = '';
      } else {
        this.selectedAddress.ruralRouteNum = this.ruralOrPoBox;
        this.selectedAddress.poBox = '';
      }
    }

    // in case province was populated manually
    this.selectedAddress.territoryCd = this.selectedAddress.territory;
    // Ensure default country is set for manual entry
    if (!this.selectedAddress.countryCd) {
      this.selectedAddress.countryCd = 'CA';
    }
    if (!this.selectedAddress.country) {
      this.selectedAddress.country = 'Canada';
    }
    return this.selectedAddress;
  }

  getRuralOrPoBoxType(): string {
    return this.ruralOrPoBoxType;
  }

  setSelectedAddress(address: Address): void {
    this.selectedAddress = this.cloneAddress(address);
    this.populateRuralRouteAndPoBoxInfo();
  }

  validateAndDisplayErrors(): boolean {
    const models = this.addressModels();

    for (const model of models) {
      const control = model.control as SubmitTrackingFormControl;
      if (control.disabled) {
        continue;
      }

      control.submitted = true;
      control.markAsDirty();
      control.markAsTouched();
      control.updateValueAndValidity({ emitEvent: true });
    }

    this.changeDetectorRef.markForCheck();
    return models.every((model: NgModel): boolean => model.control.disabled || model.valid);
  }

  protected onViewModelChange(): void {
    this.emitAddressValue(true);
  }

  isRuralOrPoBoxEmpty(): boolean {
    return this.ruralOrPoBox === '' || this.ruralOrPoBox === null || this.ruralOrPoBox === undefined;
  }

  shouldDisableStation(): boolean {
    return this.disablePOBoxRuralRoute() || this.isRuralOrPoBoxEmpty();
  }

  onRuralOrPoB0xChange(): void {
    if (this.isRuralOrPoBoxEmpty()) {
      this.selectedAddress.postalStationTxt = '';
    }

    this.emitAddressValue(true);
  }

  protected isDisabled(): boolean {
    return Boolean(this.disabled()) || this.disabledByOwner();
  }

  disablePOBoxRuralRoute(): boolean {
    const usageId = this.usageId();
    return !((usageId === 'home') || (usageId === 'mailing' && this.addressService.RURAL_PO_TYPES.some(
      (aType: string) => aType === this.ruralOrPoBoxType)));
  }

  isPoBoxRuralRouteRequired(): boolean {
    return this.usageId() !== 'home' && this.addressService.RURAL_PO_TYPES.some(
      (aType: string) => aType === this.ruralOrPoBoxType);
  }

  private populateRuralRouteAndPoBoxInfo(): void {
    this.streetNumDisplay = [this.selectedAddress.streetNum, this.selectedAddress?.streetNumSuffixVal].join('');

    const showRuralRouteAndPOBox = this.showRuralRouteAndPOBox();
    if (this.selectedAddress.ruralRouteNum && showRuralRouteAndPOBox) {
      this.ruralOrPoBox = this.selectedAddress.ruralRouteNum;
      this.ruralOrPoBoxType = AddressService.RURAL_ROUTE_TYPE;
    } else if (this.selectedAddress.poBox && showRuralRouteAndPOBox) {
      this.ruralOrPoBox = this.selectedAddress.poBox;
      this.ruralOrPoBoxType = AddressService.PO_BOX_TYPE;
    } else {
      this.ruralOrPoBox = '';
      this.ruralOrPoBoxType = '';
    }
  }

  private emitAddressValue(fromView: boolean): void {
    const selectedAddress = this.cloneAddress(this.getSelectedAddress());
    this.value.set(selectedAddress);

    if (fromView) {
      this.onChangeCallback(selectedAddress);
      this.onTouchedCallback();
      this.touch.emit();
    }
  }

  private cloneAddress(address: Address | null | undefined): Address {
    const source = address ?? AddressComponent.createEmptyAddress();
    return {
      ...source,
      addressLine: [...(source.addressLine ?? [])]
    };
  }
}
